import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  Checkbox,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import GetAppIcon from "@material-ui/icons/GetApp";
import PeopleIcon from "@material-ui/icons/People";
import ListAltIcon from "@material-ui/icons/ListAlt";
import SendIcon from "@material-ui/icons/Send";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { i18n } from "../../translate/i18n";
import CampaignProModal from "../../components/CampaignProModal";
import OfficialCampaignModal from "../../components/OfficialCampaignModal";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(0, 2),
    minHeight: 0,
  },
  backButton: { marginRight: theme.spacing(1) },
  card: {
    flex: 1,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: 0,
    overflow: "hidden",
    boxShadow: "none",
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  cardHeader: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2, 3),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexShrink: 0,
  },
  cardHeaderIcon: {
    fontSize: 28,
    opacity: 0.95,
  },
  cardHeaderTitle: {
    fontWeight: 600,
    fontSize: "1.25rem",
  },
  section: {
    flex: 1,
    padding: theme.spacing(2, 3),
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "auto",
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: "0.95rem",
    flexShrink: 0,
  },
  sectionTitleIcon: {
    color: theme.palette.primary.main,
    fontSize: 22,
  },
  toolbar: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(2),
    flexShrink: 0,
  },
  exportBtn: {
    textTransform: "none",
    borderRadius: 8,
    padding: theme.spacing(1, 2),
  },
  tableWrap: {
    flex: 1,
    minHeight: 0,
    borderRadius: 8,
    overflow: "auto",
    border: `1px solid ${theme.palette.divider}`,
    ...theme.scrollbarStyles,
  },
  table: { minWidth: 400 },
  tableHead: {
    backgroundColor: theme.palette.grey[100],
    "& th": {
      fontWeight: 600,
      fontSize: "0.875rem",
      color: theme.palette.text.secondary,
      padding: theme.spacing(1.5, 2),
    },
  },
  tableRow: {
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  },
  tableCell: {
    padding: theme.spacing(1.5, 2),
    fontSize: "0.875rem",
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  progressMessage: { marginTop: theme.spacing(2) },
}));

function processSSEBuffer(bufferRef, chunk, onEvent) {
  bufferRef.current += chunk.replace(/\r\n/g, "\n");
  const idx = bufferRef.current.lastIndexOf("\n\n");
  if (idx === -1) return;
  const complete = bufferRef.current.slice(0, idx);
  bufferRef.current = bufferRef.current.slice(idx + 2);
  complete.split("\n\n").forEach((block) => {
    const line = block.split("\n").find((l) => l.startsWith("data:"));
    if (line) {
      try {
        onEvent(JSON.parse(line.slice(5).trim()));
      } catch (_) {}
    }
  });
}

const GroupParticipants = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { groupId } = useParams();
  const params = new URLSearchParams(location.search);
  const connectionId = params.get("connectionId");

  const [participants, setParticipants] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState({ current: 0, total: 0, message: "" });
  const [selectedNumbers, setSelectedNumbers] = useState(new Set());
  const [chooseCampaignModalOpen, setChooseCampaignModalOpen] = useState(false);
  const [creatingList, setCreatingList] = useState(false);
  const [campaignModalType, setCampaignModalType] = useState(null);
  const [createdListId, setCreatedListId] = useState(null);
  const abortRef = useRef(null);
  const sseBufferRef = useRef("");

  useEffect(() => {
    if (!connectionId || !groupId) {
      history.replace("/group-management");
      return;
    }
    const fetchGroupInfo = async () => {
      try {
        const { data } = await api.get(`/group-management/groups/${encodeURIComponent(groupId)}`, {
          params: { connectionId },
        });
        setGroupName(data?.subject || "");
      } catch (_) {
        setGroupName("");
      }
    };
    fetchGroupInfo();

    const baseURL = api.defaults.baseURL || "";
    const streamUrl = `${baseURL}/group-management/groups/${encodeURIComponent(groupId)}/participants/stream?connectionId=${connectionId}`;

    const fetchParticipantsStream = async () => {
      setProgress({ current: 0, total: 0, message: i18n.t("groupManagement.normalizingProgress") });
      const ac = new AbortController();
      abortRef.current = ac;
      sseBufferRef.current = "";
      try {
        const res = await fetch(streamUrl, { credentials: "include", signal: ac.signal });
        if (!res.ok || !res.body) {
          const { data } = await api.get(`/group-management/groups/${encodeURIComponent(groupId)}/participants`, {
            params: { connectionId },
          });
          setParticipants(Array.isArray(data) ? data : []);
          return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          processSSEBuffer(sseBufferRef, chunk, (data) => {
            if (data.type === "progress") {
              setProgress({ current: data.current || 0, total: data.total || 1, message: data.message || "" });
            } else if (data.type === "result") {
              setParticipants(Array.isArray(data.participants) ? data.participants : []);
            } else if (data.type === "error") {
              toastError({ response: { data: { message: data.message } } });
            }
          });
        }
        const rest = sseBufferRef.current.trim();
        if (rest) {
          const line = rest.split("\n").find((l) => l.startsWith("data:"));
          if (line) {
            try {
              const data = JSON.parse(line.slice(5).trim());
              if (data.type === "result") setParticipants(Array.isArray(data.participants) ? data.participants : []);
            } catch (_) {}
          }
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        try {
          const { data } = await api.get(`/group-management/groups/${encodeURIComponent(groupId)}/participants`, {
            params: { connectionId },
          });
          setParticipants(Array.isArray(data) ? data : []);
        } catch (e) {
          toastError(e);
        }
      } finally {
        setLoading(false);
        abortRef.current = null;
      }
    };

    fetchParticipantsStream();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [connectionId, groupId, history]);

  const toggleSelectAll = () => {
    if (selectedNumbers.size === participants.length) {
      setSelectedNumbers(new Set());
    } else {
      setSelectedNumbers(new Set(participants.map((p) => (p.number || p.id || "").toString().replace(/\D/g, "")).filter(Boolean)));
    }
  };

  const toggleSelect = (num) => {
    const key = (num || "").toString().replace(/\D/g, "");
    if (!key) return;
    setSelectedNumbers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isSelected = (p) => selectedNumbers.has((p.number || p.id || "").toString().replace(/\D/g, ""));

  const handleGerarCampanha = () => {
    if (selectedNumbers.size === 0) {
      toast.error(i18n.t("groupManagement.selectAtLeastOneParticipant"));
      return;
    }
    setChooseCampaignModalOpen(true);
  };

  const createListAndOpenCampaign = async (type) => {
    setChooseCampaignModalOpen(false);
    const selected = participants.filter((p) => isSelected(p));
    if (selected.length === 0) return;
    setCreatingList(true);
    try {
      const baseName = groupName.trim() || i18n.t("groupManagement.listFromGroup");
      const listName = `${baseName} ${format(new Date(), "dd/MM/yyyy HH:mm")}`;
      const { data: list } = await api.post("/contact-lists-pro", {
        name: listName,
        allowDuplicates: false,
        optIn: true,
      });
      const listId = list.id;
      const BATCH = 10;
      for (let i = 0; i < selected.length; i += BATCH) {
        const batch = selected.slice(i, i + BATCH);
        await Promise.all(
          batch.map((p) =>
            api.post(`/contact-lists-pro/${listId}/items`, {
              number: (p.number || p.id || "").toString().replace(/\D/g, ""),
              name: p.name || null,
            })
          )
        );
      }
      setCreatedListId(listId);
      setCampaignModalType(type);
    } catch (err) {
      toastError(err);
    } finally {
      setCreatingList(false);
    }
  };

  const closeCampaignModal = () => {
    setCampaignModalType(null);
    setCreatedListId(null);
  };

  const exportCsv = () => {
    const headers = ["Nome", "Número", "Admin"];
    const rows = participants.map((p) => [
      (p.name || "").replace(/"/g, '""'),
      p.number || "",
      p.isAdmin || p.isSuperAdmin ? "Sim" : "Não",
    ]);
    const csv = [headers.join(";"), ...rows.map((r) => r.map((c) => `"${c}"`).join(";"))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `participantes-grupo-${groupId.replace(/@g\.us$/, "")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!connectionId || !groupId) return null;

  const progressPercent =
    progress.total > 0 ? Math.min(100, Math.round((progress.current / progress.total) * 100)) : undefined;

  return (
    <MainContainer fullWidth>
      <MainHeader>
        <Button
          className={classes.backButton}
          onClick={() => history.push(`/group-management/groups?connectionId=${connectionId}`)}
          startIcon={<ArrowBackIcon />}
        >
          {i18n.t("groupManagement.back")}
        </Button>
        <Title>{i18n.t("groupManagement.participants")}</Title>
      </MainHeader>

      <Dialog open={chooseCampaignModalOpen} onClose={() => setChooseCampaignModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{i18n.t("groupManagement.chooseCampaignType")}</DialogTitle>
        <DialogContent style={{ paddingBottom: 24 }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
            {i18n.t("groupManagement.chooseCampaignTypeHint")}
          </Typography>
          <Box display="flex" flexDirection="column" gap={3}>
            <Button variant="contained" color="primary" onClick={() => createListAndOpenCampaign("pro")} fullWidth size="medium" style={{ padding: "10px 16px" }}>
              {i18n.t("groupManagement.campaignPro")}
            </Button>
            <Button variant="outlined" color="primary" onClick={() => createListAndOpenCampaign("official")} fullWidth size="medium" style={{ padding: "10px 16px" }}>
              {i18n.t("groupManagement.campaignOfficial")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {creatingList && (
        <Dialog open disableEscapeKeyDown PaperProps={{ style: { minWidth: 320 } }}>
          <DialogTitle>{i18n.t("groupManagement.creatingList")}</DialogTitle>
          <DialogContent>
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={24} />
              <Typography variant="body2">{i18n.t("groupManagement.creatingListHint")}</Typography>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {campaignModalType === "pro" && createdListId && (
        <CampaignProModal
          open={true}
          onClose={closeCampaignModal}
          initialContactListProId={createdListId}
          resetPagination={() => {}}
        />
      )}
      {campaignModalType === "official" && createdListId && (
        <OfficialCampaignModal
          open={true}
          onClose={closeCampaignModal}
          onSuccess={closeCampaignModal}
          initialContactListProId={createdListId}
        />
      )}

      <Dialog open={loading} disableEscapeKeyDown PaperProps={{ style: { minWidth: 320 } }}>
        <DialogTitle>{i18n.t("groupManagement.normalizingTitle")}</DialogTitle>
        <DialogContent>
          {progress.total > 0 && progressPercent !== undefined && (
            <Typography variant="h6" color="primary" style={{ marginBottom: 8 }}>
              {progress.current} / {progress.total} — {progressPercent}%
            </Typography>
          )}
          <LinearProgress
            variant={progressPercent !== undefined ? "determinate" : "indeterminate"}
            value={progressPercent}
            style={{ minWidth: 280 }}
          />
          {progress.total > 0 && progressPercent === undefined && (
            <Typography variant="body2" color="primary" style={{ marginTop: 8 }}>
              {progress.current} / {progress.total}
            </Typography>
          )}
          <Typography className={classes.progressMessage} color="textSecondary" variant="body2" style={{ marginTop: 12 }}>
            {progress.message || i18n.t("groupManagement.normalizingProgress")}
          </Typography>
        </DialogContent>
      </Dialog>

      <Box className={classes.root}>
        <Paper className={classes.card}>
          <Box className={classes.cardHeader}>
            <PeopleIcon className={classes.cardHeaderIcon} />
            <Typography className={classes.cardHeaderTitle}>
              {i18n.t("groupManagement.participants")}
            </Typography>
          </Box>

          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              <ListAltIcon className={classes.sectionTitleIcon} />
              Lista de participantes
            </Typography>

            <Box className={classes.toolbar}>
              <Button
                className={classes.exportBtn}
                startIcon={<SendIcon />}
                onClick={handleGerarCampanha}
                variant="contained"
                color="primary"
                size="medium"
                disabled={loading || participants.length === 0 || selectedNumbers.size === 0}
              >
                {i18n.t("groupManagement.generateCampaign")}
              </Button>
              <Button
                className={classes.exportBtn}
                startIcon={<GetAppIcon />}
                onClick={exportCsv}
                variant="outlined"
                color="primary"
                size="medium"
                disabled={loading || participants.length === 0}
              >
                {i18n.t("groupManagement.exportCsv")}
              </Button>
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} className={classes.tableWrap}>
                <Table className={classes.table} size="medium" stickyHeader>
                  <TableHead>
                    <TableRow className={classes.tableHead}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedNumbers.size > 0 && selectedNumbers.size < participants.length}
                          checked={participants.length > 0 && selectedNumbers.size === participants.length}
                          onChange={toggleSelectAll}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>{i18n.t("groupManagement.participantName")}</TableCell>
                      <TableCell>{i18n.t("groupManagement.participantNumber")}</TableCell>
                      <TableCell align="center">{i18n.t("groupManagement.admin")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {participants.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className={classes.emptyState}>
                          <Typography variant="body2">{i18n.t("groupManagement.noParticipants")}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      participants.map((p) => (
                        <TableRow key={p.id || p.number} className={classes.tableRow}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected(p)}
                              onChange={() => toggleSelect(p.number || p.id)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell className={classes.tableCell}>{p.name || "-"}</TableCell>
                          <TableCell className={classes.tableCell}>{p.number || p.id || "-"}</TableCell>
                          <TableCell className={classes.tableCell} align="center">
                            {p.isSuperAdmin ? "Super" : p.isAdmin ? "Sim" : "-"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Paper>
      </Box>
    </MainContainer>
  );
};

export default GroupParticipants;
